import { useLayoutEffect, type MutableRefObject } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useOrg } from './app-context';

/**
 * What has to happen when the org under the app changes.
 *
 * Two things, and both are consequences of decisions made elsewhere. Every
 * query key but `['me']` carries the org id at index 1 (`api/keys.ts`), so the
 * old org's reads would sit in the cache being neither shown nor collected —
 * they are dropped outright rather than left to expire. And SPA routes are
 * org-less (ui-design §6.4), so `/people/:id` after a switch names a subject the
 * new org cannot see; the reader goes back to Home rather than to a 404 they
 * did not ask for.
 *
 * It handles both ways the org can change — the reader picking another one, and
 * the forced fallback when membership ends under them — because from here they
 * are the same event. Mounted inside the router, so `useNavigate` has one.
 *
 * The "which org were we under" ref is owned by `Bootstrap` and passed in,
 * because this component does not survive an org change. Bootstrap early-returns
 * on a pending `/me` (every `queryClient.clear()`, e.g. a dev-user switch), on a
 * failed poll, and on no org — each unmounts the whole router subtree. A ref held
 * here would be reborn equal to the new org, and the remount would look like a
 * first scope: no purge, no navigate, the reader left on the old org's URL.
 */
export function OrgScope({ scoped }: { scoped: MutableRefObject<string | null> }) {
  const org = useOrg();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Layout, not passive, so the route resets before paint: the old route never
  // shows a frame under the new org. It does not stop the one stray read the
  // still-mounted route fires during the render pass (TanStack executes the
  // fetch when the key changes, ahead of any effect); that read 404s under the
  // new org and is dropped with the route, never shown.
  useLayoutEffect(() => {
    // First scope of the session — the app is already rendering under this org,
    // so there is nothing to reset.
    if (scoped.current === null) {
      scoped.current = org.id;
      return;
    }
    if (scoped.current === org.id) return;
    scoped.current = org.id;
    queryClient.removeQueries({
      predicate: (query) => query.queryKey[0] !== 'me' && query.queryKey[1] !== org.id,
    });
    navigate('/', { replace: true });
  }, [org.id, queryClient, navigate, scoped]);

  return null;
}
